/**
 * The launch sequence — a live 7-step stepper driven by EnrollmentResponse.steps.
 *
 * Self-contained: takes the response via props, surfaces no side effects,
 * imports nothing from redux.
 */

import { useEffect, useState } from "react"
import {
  Check,
  X,
  Loader2,
  SkipForward,
  Building2,
  Landmark,
  UserRound,
  Sliders,
  Layers,
  Workflow,
  Boxes,
  Rocket,
  CreditCard
} from "lucide-react"
import {
  ENROLLMENT_STEP_LABELS,
  ENROLLMENT_STEP_ORDER,
  EnrollmentResponse,
  EnrollmentStep,
  EnrollmentStepName,
  StepStatus
} from "../types"

interface Props {
  enrollment: EnrollmentResponse
  /** True while the page is in the `polling` state, so we keep the elapsed timer running. */
  isLive: boolean
}

const STEP_ICONS: Record<EnrollmentStepName, React.ReactNode> = {
  org: <Building2 size={16} />,
  brand: <Landmark size={16} />,
  lo: <UserRound size={16} />,
  rateflow: <Sliders size={16} />,
  profiles: <Layers size={16} />,
  workflow: <Workflow size={16} />,
  embeds: <Boxes size={16} />,
  billing: <CreditCard size={16} />
}

const formatId = (
  step: EnrollmentStep,
  stepName: EnrollmentStepName
): string | null => {
  if (step.status !== "success" || step.id == null) return null
  // profiles.id and embeds.id are COUNTS not node ids — footgun called out in API doc
  if (stepName === "profiles") return `${step.id} profiles created`
  if (stepName === "embeds") return `${step.id} embeds created`
  return `#${step.id}`
}

const formatElapsed = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`
  const s = ms / 1000
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  return `${m}m ${Math.floor(s - m * 60)}s`
}

const StepRow = ({
  index,
  stepName,
  step
}: {
  index: number
  stepName: EnrollmentStepName
  step: EnrollmentStep
}) => {
  const status: StepStatus = step.status

  const statusStyles: Record<StepStatus, string> = {
    success: "border-l-emerald-500 bg-white",
    failed: "border-l-red-500 bg-red-50",
    started: "border-l-primary-400 bg-primary-50/40 animate-pulse",
    pending: "border-l-neutral-200 bg-white opacity-60",
    skipped: "border-l-neutral-300 bg-neutral-50 opacity-70"
  }

  const indicator = (() => {
    if (status === "success") {
      return (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white">
          <Check size={14} strokeWidth={3} />
        </span>
      )
    }
    if (status === "failed") {
      return (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white">
          <X size={14} strokeWidth={3} />
        </span>
      )
    }
    if (status === "skipped") {
      return (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-300 text-white">
          <SkipForward size={14} />
        </span>
      )
    }
    if (status === "started") {
      return (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-white">
          <Loader2 size={14} className="animate-spin" />
        </span>
      )
    }
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-neutral-300 text-neutral-400">
        {STEP_ICONS[stepName]}
      </span>
    )
  })()

  const idLabel = formatId(step, stepName)

  return (
    <li
      className={`relative pl-4 pr-4 py-3 border-l-2 transition-colors ${statusStyles[status]}`}
    >
      <div className="flex items-center gap-3">
        {indicator}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span
              className={`text-sm font-medium truncate ${
                status === "pending" || status === "skipped"
                  ? "text-neutral-400"
                  : "text-neutral-900"
              }`}
            >
              {ENROLLMENT_STEP_LABELS[stepName]}
              {status === "skipped" && (
                <span className="ml-1.5 text-xs font-normal text-neutral-400">
                  — skipped
                </span>
              )}
            </span>
          </div>
          {idLabel && (
            <div className="mt-0.5 text-xs text-neutral-500 font-mono">
              {idLabel}
            </div>
          )}
          {status === "failed" && step.error_message && (
            <div className="mt-1 text-xs text-red-700 leading-snug">
              {step.error_message}
            </div>
          )}
        </div>
      </div>
    </li>
  )
}

const EnrollmentProgress = ({ enrollment, isLive }: Props) => {
  const [now, setNow] = useState(() => Date.now())

  // Tick the elapsed timer once a second while live
  useEffect(() => {
    if (!isLive) return
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [isLive])

  const startedMs = enrollment.started_at * 1000
  const updatedMs = enrollment.updated_at * 1000
  const elapsed = isLive ? now - startedMs : updatedMs - startedMs

  const successCount = ENROLLMENT_STEP_ORDER.filter(
    name => enrollment.steps[name]?.status === "success"
  ).length
  const skippedCount = ENROLLMENT_STEP_ORDER.filter(
    name => enrollment.steps[name]?.status === "skipped"
  ).length

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      {/* Header */}
      <header className="px-6 py-5 border-b border-neutral-100 bg-gradient-to-br from-primary-50 to-white">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-white">
            <Rocket size={18} />
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <h2 className="font-serif text-lg text-neutral-900">
                Enrollment #{enrollment.enrollment_id}
              </h2>
              {isLive && (
                <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-primary-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-500 animate-pulse" />
                  Live
                </span>
              )}
            </div>
            <p className="text-xs text-neutral-500 mt-0.5">
              {successCount} of {ENROLLMENT_STEP_ORDER.length} steps complete
              {skippedCount > 0 && ` (${skippedCount} skipped)`} ·{" "}
              <span className="font-mono">{formatElapsed(elapsed)}</span>
            </p>
          </div>
        </div>
      </header>

      {/* Step list */}
      <ol className="divide-y divide-neutral-100">
        {ENROLLMENT_STEP_ORDER.map((name, idx) => (
          <StepRow
            key={name}
            index={idx}
            stepName={name}
            step={
              enrollment.steps[name] ?? {
                status: "pending",
                id: null,
                error: null,
                details: null
              }
            }
          />
        ))}
      </ol>
    </div>
  )
}

export default EnrollmentProgress
